import { Formik, Form } from 'formik'
import { Persist } from 'formik-persist'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled, { createGlobalStyle } from 'styled-components'
import * as Yup from 'yup'
import AlertBanner from '@/components/alert-banner'
import { Anchor } from '@/components/anchor'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import { TextLink } from '@/components/button'
import ContentWrapper from '@/components/content-wrapper'
import { Row, Group, Input, FieldDatePicker } from '@/components/form'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import config from '@/config'
import ListingInfo from '@/features/Listing/listing-info'
import TenantSummary from '@/features/Profile/TenantSummary'
import useActiveApplications from '@/hooks/use-active-applications'
import useMediaQuery from '@/hooks/use-media-query'
import useProfile from '@/hooks/use-profile'
import { breakpoints, colors, shadows, structure } from '@/styles'

const { WEB_URL } = config

const Background = createGlobalStyle`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    body {
      background: ${colors.white};
    }
  }
`

const AccountInfo = styled.div`
  position: sticky;
  background: ${colors.white};
  box-shadow: ${shadows.header};
  z-index: 2;
  max-height: 215px;
  overflow: hidden;
  top: ${structure.header.height}px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    top: ${structure.header.heightMobile}px;
  }
`

const Wrapper = styled(ContentWrapper)`
  margin-top: ${props => (props.transparent ? 25 : 50)}px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    section {
      border: none;
    }
    margin: 0;
    padding: 0 10px;
    max-width: none;
    width: 100%;
  }
`

const Checkbox = styled(Input)``

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  margin-right: 40px;

  ${Checkbox} {
    margin-left: 20px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0;

    ${Checkbox} {
      margin-left: 10px;
    }
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 0 10px 0 0;
  }
`

const StyledPanel = styled(Panel)`
  ${props =>
    props.transparent &&
    `
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
  `}
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    border: 0;
    box-shadow: none;
  }
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-top: 32px;
    > div:first-of-type {
      display: none;
    }
  }
`

const StyledAlertBanner = styled(AlertBanner)`
  margin-bottom: 25px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0;
    > div {
      justify-content: center;
      border-radius: 0;
    }
  }
`

const ProfileLink = styled(TextLink)`
  margin-top: 0;
  padding: 5px 0;
`

const HeaderText = styled.span`
  font-size: 14px;
  margin: 0;
  padding: 3px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const ApplicationOffer = ({
  match: {
    params: { listingId }
  },
  history
}) => {
  const dispatch = useDispatch()
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const data = useProfile()
  const { t } = useTranslation()
  const activeApplications = useActiveApplications(listingId)
  const hasAlreadyApplied = activeApplications?.length > 0
  const completion = data.completion_percentage ?? 0
  const hasCompletedProfile = completion === 100
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const colSize = displayMobile ? 12 : 6

  useEffect(() => {
    dispatch.listing.load(listingId)
  }, [dispatch.listing, listingId])

  useEffect(() => {
    if (hasAlreadyApplied) history.replace(`/application/${activeApplications[0].id}`)
  }, [hasAlreadyApplied, activeApplications, history])

  const validationSchema = Yup.object().shape({
    move_in_date: Yup.date()
  })
  const initialValues = {
    move_in_date: '',
    message: "Hi, I'm interested in your unit, please see my tenancy application."
  }

  if (!data.id) return null

  const onSubmit = async () => {
    history.push(`/application/create/${listingId}/preview`)
  }

  const Terms = () =>
    hasCompletedProfile ? (
      <CheckboxContainer>
        <div>
          <Trans i18nKey="b.application.view.agree_to_terms_and_conditions.text">
            I have read and agree to the{' '}
            <Anchor as="a" href={`${WEB_URL}/terms`} target="_blank" rel="noopener noreferrer">
              Terms & Conditions.
            </Anchor>
          </Trans>
        </div>
        <Checkbox
          data-cy="agree_to_terms"
          type="checkbox"
          checked={acceptedTerms}
          onChange={e => setAcceptedTerms(e.target.checked)}
        />
      </CheckboxContainer>
    ) : null

  return (
    <Page>
      <Helmet>
        <title>{t('b.application.view.tenancy_application.title', 'Tenancy Application')}</title>
      </Helmet>
      <PageTitle>
        {t('b.application.view.tenancy_application.title', 'Tenancy Application')}
      </PageTitle>
      <Background />
      <AccountInfo>
        <TenantSummary data={data} editable isHeader hideContact>
          <HeaderText>
            {hasCompletedProfile
              ? t(
                  'r.application.create.completed_profile.label',
                  'Your profile is now 100% complete!'
                )
              : t(
                  'r.application.create.not_complete_profile.label',
                  'Your profile is not completed yet.'
                )}
          </HeaderText>
          {hasCompletedProfile ? (
            <ProfileLink
              to={{
                pathname: '/profile/basic-info',
                state: { onBackRedirectTo: `/application/create/${listingId}` }
              }}
            >
              {t('r.application.create.edit_profile.link', 'Edit profile')}
            </ProfileLink>
          ) : (
            <ProfileLink to={`/application/create/${listingId}/complete`}>
              {t('r.application.create.complete_profile.link', 'Complete my profile')}
            </ProfileLink>
          )}
        </TenantSummary>
      </AccountInfo>
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit}>
        {({ isValid, values }) => (
          <Form className="flex-full">
            <Wrapper className="mb-50" transparent={!hasCompletedProfile}>
              {!hasCompletedProfile && (
                <StyledAlertBanner
                  width="100%"
                  justifyContent="flex-start"
                  type="warning"
                  message={t(
                    'r.application.create.apply_complete_profile.label',
                    'To apply, please complete your profile first.'
                  )}
                />
              )}
              <StyledPanel
                hSpace={displayMobile ? 40 : 30}
                vSpace={30}
                transparent={!hasCompletedProfile}
              >
                <ListingInfo listingId={listingId} />
                <Row top={displayMobile ? 30 : 50} bottom={30}>
                  <Group col={colSize}>
                    <FieldDatePicker
                      name="move_in_date"
                      disablePast
                      disabled={!hasCompletedProfile}
                      placeholder={t(
                        'r.application.create.move_in_date.label',
                        'Desired move-in date'
                      )}
                      required
                      emptyLabel={t(
                        'r.application.create.as_soon_as_possible.label',
                        'As soon as possible'
                      )}
                      value={values.move_in_date}
                    />
                  </Group>
                </Row>
                <Terms />
              </StyledPanel>
            </Wrapper>
            <DefaultButtonBar>
              <Button
                theme="primary"
                size="small"
                type="submit"
                id="logged-preview-application-click"
                disabled={!hasCompletedProfile || !isValid || !acceptedTerms}
              >
                {displayMobile
                  ? t('r.application.create.preview_application_mobile.button', 'Preview')
                  : t('r.application.create.preview_application.button', 'Preview Application')}
              </Button>
            </DefaultButtonBar>
            <Persist name={`create-application-${listingId}`} isSessionStorage />
          </Form>
        )}
      </Formik>
    </Page>
  )
}

export default ApplicationOffer
